/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Component, OnInit, ViewChild, Input, ElementRef, ChangeDetectorRef} from '@angular/core';
import {ActivatedRoute} from '@angular/router';

import {EmGroup} from "../models/em-group";
import {GroupType} from '../models/group-type';
import {InstanceData} from "../models/instance-data";
import {ObjectCategory} from '../models/object-category';
import {ObjectDefinition} from '../models/object-definition';
import {ObjectInstance} from '../models/object-instance';
import {TagInstance} from "../models/tag-instance";

import {GroupService} from '../services/group.service';
import {ObjectService} from '../services/object.service';
import {TagService} from "../services/tag.service";

import {ConfirmDeleteDialogComponent} from './confirm-delete-dialog.component';
import {ManageGroupsDialogComponent} from './manage-groups-dialog.component';
import {ShowGroupDialogComponent} from './show-group-dialog.component';
import {AddToGroupDialogComponent} from './add-to-group-dialog.component';
import {RemoveFromGroupDialogComponent} from './remove-from-group-dialog.component';

import {EntityInstanceDialogComponent} from './infrastructure/dialogs/entity-instance-dialog.component';
import {EntityTypeDialogComponent} from "./infrastructure/dialogs/entity-type-dialog.component";
import { INFRA_DUMMY_FUNCTION, RESULTS_PER_PAGE } from "../app-constants";
import { ImageService } from 'app/services/image.service';
import { PagedResults } from 'app/models/paged-results';
import { NgxSpinnerService } from 'ngx-spinner';
import { FunctionParameterDataUi } from './infrastructure/dialogs/general-function-dialog.component';
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from './simple-success-error-dialog-box/simple-success-error-dialog-box.component';
import { FilterOption, FilterType, REMOVE_FILTER } from '../models/filter-option';

@Component({
  templateUrl: './main-people.component.html',
  selector: 'app-main-people-component',
  styleUrls: ['../../assets/css/infrastructure.css']
})
export class MainPeopleComponent implements OnInit {

  @ViewChild('confirmDeleteDialog')
  mConfirmDeleteDialog: ConfirmDeleteDialogComponent;

  @ViewChild('showGroupDialog')
  mShowGroupDialog: ShowGroupDialogComponent;

  @ViewChild('manageGroupsDialog')
  mManageGroupsDialog: ManageGroupsDialogComponent;

  @ViewChild('addToGroupDialog')
  mAddToGroupDialogComponent: AddToGroupDialogComponent;

  @ViewChild('removeFromGroupDialog')
  mRemoveFromGroupDialogComponent: RemoveFromGroupDialogComponent;

  @ViewChild('entityTypeDialog')
  mEntityTypeDialogComponent: EntityTypeDialogComponent;

  @ViewChild('entityInstanceDialog')
  mEntityInstanceDialogComponent: EntityInstanceDialogComponent;

  @ViewChild('fileTypeUpload')
  mTypeFileUploadInput: ElementRef;

  @ViewChild('fileInstanceUpload')
  mInstanceFileUploadInput: ElementRef;

    /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  @Input()
  displayHeader = true;

  mPeopleTypeUis: PeopleObjectUI[] = [];
  mPeopleInstanceUis: PeopleInstanceUI[] = [];

  mDeleteFlag = 0; //1 = Peoples, 2 = People Instances

  //Entity Type Dialog Variables
  mEntity: ObjectDefinition;
  mEntityName: string;
  mEntityType = GroupType.OBJECT;
  mEntityTypeName = "People";

  /** Filter Options available to the Encoding Scheme Types */
  filterOptionsForTypes: FilterOption[] = [];
  /** Selected Filter Option for the Encoding Scheme Types */
  filterOptionForTypes: FilterOption = REMOVE_FILTER;
  /** Filter options available to the Encoding Scheme Instances */
  filterOptionsForInstances: FilterOption[] = [];
  /** Selected Filter Option for the Encoding Scheme Instances */
  filterOptionForInstances: FilterOption = REMOVE_FILTER;
  /** Active filters for the People Instances (Name + Type), AND'd together */
  activeFilterOptionsForInstances: FilterOption[] = [];

  //Entity Instance Dialog Variables
  mInstance: ObjectInstance;
  mInstanceData: InstanceData[];
  mInstanceName: string;
  mInstanceEntityType: ObjectDefinition;
  mObjectCategory = ObjectCategory.PEOPLE;
  mInstanceType = GroupType.OBJECTINSTANCE;
  mInstanceTypeName = "People";
  mAvailableEntityTypes: ObjectDefinition[];

  mSelectedTags: TagInstance[];
  mAvailableTags: TagInstance[];
  mOriginalTags: TagInstance[];

  /** Total # of "Encoding Scheme" Types known to the system. (Used to figure out # of pages) */
  totalNumberOfTypes = 0;
  /** Total # of "Encoding Scheme" Instances known to the system. (Used to figure out he # of pages) */
  totalNumberOfInstances = 0;
  /** Current Page being displayed for the "Encoding Scheme" Types */
  currentPageForTypes = 1;
  /** Current Page being displayed for the "Encoding Scheme" Instances */
  currentPageForInstances = 1;

  resultsPerPageValues = RESULTS_PER_PAGE;
  selectedPageSizeValueForTypes: number = this.resultsPerPageValues[0];
  selectedPageSizeValueForInstances: number = this.resultsPerPageValues[0];

  // Variables for the dropdown menu 
  functionList = [];
  selectedFunction = [];
  showContextMenu: boolean = false;
  contextMenuPosition = { x: 0, y: 0 };

  // Variable for General Confirm/Cancel Dialog Box
  showGeneralDialog: boolean = false;
  functionParameterData: FunctionParameterDataUi[] = [];


  constructor(
    private mRoute: ActivatedRoute,
    private mObjectService: ObjectService,
    private mGroupService: GroupService,
    private mTagService: TagService,
    private mImageService: ImageService,
    private cdr: ChangeDetectorRef,
    private spinnerService: NgxSpinnerService
  ) {
  }

  ngOnInit(): void {
    this.refreshFilterOptionsForTypes();
    this.refreshFilterOptionsForInstances();
    this.selectedPageSizeValueForTypes = this.resultsPerPageValues[0];
    this.selectedPageSizeValueForInstances = this.resultsPerPageValues[0];

    this.mRoute.data.subscribe(
      (data: 
        { people: PagedResults<ObjectDefinition>, 
          peopleInstances: PagedResults<ObjectInstance>}
        ) => {     
          this.updatePagedTypes(data.people);
          this.updatePagedInstances(data.peopleInstances);
    });
  }

  onTypePageChange(pageNumber?: number): void {
    // Make sure the page number is valid... If not, ignore the call.
    if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
      return;
    }

    // Make sure the page number is changing... If not, ignore the call
    if (this.currentPageForTypes == pageNumber) {
      return;
    }

    // Update the current page number and refresh the data
    this.currentPageForTypes = pageNumber;
    this.refreshTypes();
  }

  private async refreshTypes(): Promise<void> {
    // Show the spinner (see this being done in the Tags page)
    this.spinnerService.show();

    // Get the results for the current page / filtering
    let pagedData: PagedResults<ObjectDefinition>;
    try {
      pagedData = await this.mObjectService.getAllObjectsForCategoryPaging(ObjectCategory.PEOPLE, (this.currentPageForTypes - 1) * this.selectedPageSizeValueForTypes, this.selectedPageSizeValueForTypes, "ASC", this.filterOptionForTypes.apiValue);
    } catch (error) {
      // Hide the Spinner / Handle the Error
      this.spinnerService.hide();
      this.handleError(error);
      return;
    }

    // Update the Page Data and hide the Spinner
    this.updatePagedTypes(pagedData);
    this.spinnerService.hide();
  }

  private updatePagedTypes(pagedData: PagedResults<ObjectDefinition>): void {
    // Create the UI Objects for the Types, update the [] being displayed by the UI
    const uiObjects: PeopleObjectUI[] = [];
    if (pagedData != null) {
      //this.mAvailableEntityTypes = pagedData.data;
      pagedData.data.forEach(people1 => {
        const ui = new PeopleObjectUI();
        console.log("UI" + ui);
        ui.mPeopleType = people1;
        ui.mSelected = false;
        uiObjects.push(ui);
      });
    }
    this.mPeopleTypeUis = uiObjects;
    console.log("mPeopleTypeUis" + this.mPeopleTypeUis);
    // Update the Pagination variables 
    this.totalNumberOfTypes = pagedData.size;
    
  }


  onInstancePageChange(pageNumber?: number): void {
    // Make sure the page number is valid... If not, ignore the call.
    if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
      return;
    }

    // Make sure the page number is changing... If not, ignore the call
    if (this.currentPageForInstances == pageNumber) {
      return;
    }

    // Update the current page number and refresh the data
    this.currentPageForInstances = pageNumber;
    this.refreshInstances();
  }

  private async refreshInstances(): Promise<void> {
    // Show the spinner (see this being done in the Tags page)
    this.spinnerService.show();

    // Get the results for the current page / filtering
    let pagedData: PagedResults<ObjectInstance>;
    try {
      pagedData = await this.mObjectService.getAllObjectInstancesForCategoryPagingWithFilters(ObjectCategory.PEOPLE, (this.currentPageForInstances - 1) * this.selectedPageSizeValueForInstances, this.selectedPageSizeValueForInstances, "ASC", this.activeFilterOptionsForInstances);
    } catch (error) {
      // Hide the Spinner / Handle the Error
      this.spinnerService.hide();
      this.handleError(error);
      return;
    }

    this.updatePagedInstances(pagedData);
    this.spinnerService.hide();
  }


  private updatePagedInstances(pagedData: PagedResults<ObjectInstance>): void {
    // Create the InstanceUI Objects for the Instances, update the [] being displayed by the UI
    const uiObjects: PeopleInstanceUI[] = [];
    //console.log("updatePagedDataInstance:" + pagedData);
    if (pagedData.data != null) {
      pagedData.data.forEach(peopleInstance => {
        const ui = new PeopleInstanceUI();
        ui.mPeopleInstance = peopleInstance;
        ui.mSelected = false;
        uiObjects.push(ui);
      });
    }
    this.mPeopleInstanceUis = uiObjects;
    this.totalNumberOfInstances = pagedData.size;

  }


  manageResultPerPageClickedForTypes(value){
    this.selectedPageSizeValueForTypes = value;
    this.currentPageForTypes = 1;
    this.refreshTypes();
  }

  manageResultPerPageClickedForInstances(value) {
    this.selectedPageSizeValueForInstances = value;
    this.currentPageForInstances = 1;
    this.refreshInstances();
  }

  private refreshFilterOptionsForTypes(): void {
    this.filterOptionsForTypes = [
      REMOVE_FILTER,
      new FilterOption({ column: "name", label: "Name", type: FilterType.STRING }),
    ];
  }

  private refreshFilterOptionsForInstances(): void {
    this.filterOptionsForInstances = [
      REMOVE_FILTER,
      new FilterOption({ column: "name", label: "Name", type: FilterType.STRING }),
      new FilterOption({ column: "object.name", label: "Type", type: FilterType.STRING }),
    ];
  }


  isSinglePeopleTypeSelected(): boolean {
    if (this.mPeopleTypeUis != null && this.mPeopleTypeUis.length > 0) {
      let oneSelected = false;
      for (const PeopleObject of this.mPeopleTypeUis) {
        if (PeopleObject.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyPeopleTypesSelected(): boolean {
    return this.mPeopleTypeUis.some(PeopleObject => PeopleObject.mSelected);
  }

  isAllPeopleTypesSelected(): boolean {
    return this.mPeopleTypeUis !== null && this.mPeopleTypeUis.every(PeopleObject => PeopleObject.mSelected);
  }

  setAllPeopleTypesSelected(event: any): void {
    this.mPeopleTypeUis.forEach(PeopleObject => PeopleObject.mSelected = event.target.checked);
  }


  getSelectedPeopleTypes(): ObjectDefinition[] {
    const Peoples: ObjectDefinition[] = [];
    this.mPeopleTypeUis.filter(ui => ui.mSelected).forEach(ui => Peoples.push(ui.mPeopleType));
    return Peoples;
  }

  getSelectedPeopleTypeNames(): string[] {
    const names: string[] = [];
    this.mPeopleTypeUis.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mPeopleType.name));
    return names;
  }

  getSelectedPeopleTypeIds(): number[] {
    const ids: number[] = [];
    this.mPeopleTypeUis.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mPeopleType.id));
    return ids;
  }


  managePeopleTypeGroups(): void {
    this.mManageGroupsDialog.show(GroupType.OBJECT);
  }

  addPeopleTypesToGroupClicked(): void {
    if (this.isAnyPeopleTypesSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.OBJECT, this.getSelectedPeopleTypeNames());
    }
  }

  removePeopleTypesFromGroupClicked(): void {
    if (this.isAnyPeopleTypesSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const PeopleTypeNames: string[] = [];
      this.getSelectedPeopleTypes().forEach(PeopleType => {
        PeopleTypeNames.push(PeopleType.name);
        if (PeopleType.groups != null) {
          PeopleType.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, PeopleTypeNames);
    }
  }


  isSinglePeopleInstanceSelected(): boolean {
    if (this.mPeopleInstanceUis != null && this.mPeopleInstanceUis.length > 0) {
      let oneSelected = false;
      for (const PeopleInstances of this.mPeopleInstanceUis) {
        if (PeopleInstances.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyPeopleInstancesSelected(): boolean {
    return this.mPeopleInstanceUis.some(PeopleInstance => PeopleInstance.mSelected);
  }

  isAllPeopleInstancesSelected(): boolean {
    return this.mPeopleInstanceUis !== null && this.mPeopleInstanceUis.every(PeopleInstance => PeopleInstance.mSelected);
  }

  setAllPeopleInstancesSelected(event: any): void {
    this.mPeopleInstanceUis.forEach(PeopleInstance => PeopleInstance.mSelected = event.target.checked);
  }

  getSelectedPeopleInstances(): ObjectInstance[] {
    const PeopleInstances: ObjectInstance[] = [];
    this.mPeopleInstanceUis.filter(ui => ui.mSelected).forEach(ui => PeopleInstances.push(ui.mPeopleInstance));
    return PeopleInstances;
  }

  getSelectedPeopleInstanceNames(): string[] {
    const names: string[] = [];
    this.mPeopleInstanceUis.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mPeopleInstance.name));
    return names;
  }

  getSelectedPeopleInstanceIds(): number[] {
    const ids: number[] = [];
    this.mPeopleInstanceUis.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mPeopleInstance.id));
    return ids;
  }


  managePeopleInstanceGroups(): void {
    this.mManageGroupsDialog.show(GroupType.OBJECTINSTANCE);
  }

  addPeopleInstancesToGroupClicked(): void {
    if (this.isAnyPeopleInstancesSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.OBJECTINSTANCE, this.getSelectedPeopleInstanceNames());
    }
  }

  removePeopleInstancesFromGroupClicked(): void {
    if (this.isAnyPeopleInstancesSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const PeopleInstanceNames: string[] = [];
      this.getSelectedPeopleInstances().forEach(PeopleInstance => {
        PeopleInstanceNames.push(PeopleInstance.name);
        if (PeopleInstance.groups != null) {
          PeopleInstance.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, PeopleInstanceNames);
    }
  }


  addSelectedToGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.OBJECT: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedPeopleTypeIds())
          .then(
            success => {
              this.refreshTypes();
            },
            error => {
              const response: Response = error;
              alert(response.text());
            }
          );
        break;
      }
      case GroupType.OBJECTINSTANCE: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedPeopleInstanceIds())
          .then(
            success => {
              this.refreshInstances();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }

  removeSelectedFromGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.OBJECT: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedPeopleTypeIds())
          .then(
            success => {
              this.refreshTypes();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.OBJECTINSTANCE: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedPeopleInstanceIds())
          .then(
            success => {
              this.refreshInstances();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }

  showGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.OBJECT: {
        this
          .mGroupService
          .getAllObjectsInGroup(group.id)
          .then(
            result => {
              const itemNames: string[] = [];
              result.forEach(groupMember => itemNames.push(groupMember.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.OBJECTINSTANCE: {
        this
          .mGroupService
          .getAllObjectInstancesInGroup(group.id)
          .then(
            result => {
              const itemNames: string[] = [];
              result.forEach(groupMember => itemNames.push(groupMember.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }


  addPeopleClicked(): void {
    const newEntity = new ObjectDefinition();
    newEntity.category = ObjectCategory.PEOPLE;

    this.mEntity = newEntity;
    this.mEntityName = null;
    console.log("Add people  clicked");
    this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;
    this.mEntityTypeDialogComponent.mParents = this.mPeopleTypeUis.map(ui => ui.mPeopleType);
    this.mEntityTypeDialogComponent.showDialog();
  }

  editSelectedPeopleClicked(): void {
    if (this.isSinglePeopleTypeSelected()) {
      this.editPeople(this.getSelectedPeopleTypes()[0]);
    }
  }

  duplicateSelectedPeopleClicked(): void {
    if (this.isSinglePeopleTypeSelected()) {
      this
        .mObjectService
        .getObjectForId(this.getSelectedPeopleTypes()[0].id)
        .then(
          response => {
            response.id = null;
            if (response.properties != null) {
              response.properties.forEach(p => p.id = null);
            }
            if (response.functions != null) {
              response.functions.forEach(f => f.id = null);
            }

            this.mEntity = response;
            this.mEntityName = null;
            this.mEntityTypeDialogComponent.showDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  deleteSelectedPeopleClicked(): void {
    if (this.isAnyPeopleTypesSelected()) {
      this.mDeleteFlag = 1; //1 = People
      this.mConfirmDeleteDialog.show("People type", this.getSelectedPeopleTypeNames());
    }
  }

  async editPeople(person: ObjectDefinition): Promise<void> {
    //Get the latest values from the server
    let objectType: ObjectDefinition = null;
    try {
      objectType = await this.mObjectService.getObjectForId(person.id);
    } catch (error) {
      this.handleError(error);
      return;
    }

    //Display the Dialog to edit the Person Type
    if (objectType != null) {
      //Update the reference to the "Parent" Type to the one that will be in the list of options if the Person Type has one
      if (objectType.parent != null) {
        const parentUi = this.mPeopleTypeUis.find(ui => ui.mPeopleType.id == objectType.parent.id);
        if (parentUi == null) {
          objectType.parent = null;
        } else {
          objectType.parent = parentUi.mPeopleType;
          console.log("Setting Parent Type to '" + objectType.parent.id + "/" + objectType.parent.name + "'...");
        }
      }

      this.mEntity = objectType;
      this.mEntityName = objectType.name;
      if (objectType.parent != null) {
        this.mEntityTypeDialogComponent.entityParent = objectType.parent;
      } else {
        this.mEntityTypeDialogComponent.entityParent = null;
      }
      this.mEntityTypeDialogComponent.mParents = this.mPeopleTypeUis.map(ui => ui.mPeopleType);
      this.mEntityTypeDialogComponent.showDialog();
    }


  }

  createOrUpdateEntityType(): void {
    if (this.mEntity.id == null) {
      //ID = null, this is a new People... Create it
      this
        .mObjectService
        .createObject(this.mEntity)
        .then(
          response => {
            this.refreshTypes();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID <> null, this is an existing People... Update it
      this
        .mObjectService
        .updateObject(this.mEntity)
        .then(
          response => {
            this.refreshTypes();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }


  addPeopleInstanceClicked(): void {
    //Create a new Instance to be modeled in the Entity Instance Dialog
    this.mInstance = new ObjectInstance();
    this.mInstanceName = null;

    //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
    this.mEntityInstanceDialogComponent.setOriginalInstanceData([]);
    this.refreshTypesReloadDataRefreshTagsAndShowDialog();
  }

  editSelectedPeopleInstanceClicked(): void {
    if (this.isSinglePeopleInstanceSelected()) {
      this.editPeopleInstance(this.getSelectedPeopleInstances()[0]);
    }
  }

  duplicateSelectedPeopleInstanceClicked(): void {
    if (this.isSinglePeopleInstanceSelected()) {
      this
        .mObjectService
        .getObjectInstanceForId(this.getSelectedPeopleInstances()[0].id)
        .then(
          response => {
            //Set the Instance to be modeled in the Entity Instance Dialog to the Instance returned...
            this.mInstance = response;

            //Set the Instance Name value so that it gets properly displayed in the Dialog Header
            this.mInstanceName = response.name;

            //Reset the IDs of the Instance and all the Instance Data so that it gets treated as new on the Server
            this.mInstance.id = null;
            this.mInstance.uuid = null;
            if (this.mInstance.data != null) {
              this.mInstance.data.forEach(d => d.id = null);
            }

            //Refresh the Available Types, Reload the Instance Data and Show the Dialog to start editing
            this.refreshTypesReloadDataRefreshTagsAndShowDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  deleteSelectedPeopleInstancesClicked(): void {
    if (this.isAnyPeopleInstancesSelected()) {
      this.mDeleteFlag = 2; //2 = People Instances
      this.mConfirmDeleteDialog.show("People", this.getSelectedPeopleInstanceNames());
    }
  }

  editPeopleInstance(PeopleInstance: ObjectInstance): void {
    this
      .mObjectService
      .getObjectInstanceForId(PeopleInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the Entity Instance Dialog to the Instance that was returned
          this.mInstance = response;

          //Set the Instance Name which is displayed in the Dialog Header
          this.mInstanceName = response.name;

          // Set the original Instance Data being displayed by the EntityInstanceDialogComponent
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(this.mInstance.data);

          //Refresh the Available Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataRefreshTagsAndShowDialog();
        },
        error => this.handleError(error)
      );
  }

  async refreshTypesReloadDataRefreshTagsAndShowDialog(): Promise<void> {
    //Create a List of the available Peoples that the Instance can be a type of from the List already obtained
    this.mAvailableEntityTypes = await this.mObjectService.getAllPeopleObjects();

    //Find/Set the Entity Type the Instance is the type of
    this.mInstanceEntityType = this.mInstance.object == null ? null : this.mAvailableEntityTypes.find(entityType => entityType.id == this.mInstance.object.id);
    if (this.mInstanceEntityType != null) {
      this.mInstanceEntityType.functions = this.mInstance.object.functions;
      this.mInstanceEntityType.properties = this.mInstance.object.properties;
    }

    // Need to detect the changes here so that the "mInstanceEntityType" reference update gets passed to the EntityInstanceDialogComponent being shown below
    this.cdr.detectChanges();

    //Refresh the Instance Data, loading the existing values in for the Instance being edited
    this.mEntityInstanceDialogComponent.refreshData();

    //Set the Tag that is assigned to the People
    this.mOriginalTags = [];
    this.mSelectedTags = [];
    this.mAvailableTags = [];
    if (this.mInstance == null || this.mInstance.id == null) {
      this.refreshTagsReloadDataAndShowDialog();
    } else {
      this
        .mObjectService
        .getAssignedTagsForObjectInstance(this.mInstance.id)
        .then(
          assignedTags => {
            //Store the Tag that is assigned to the People
            console.log("Got tags for objectInstance" + this.mInstance.id);
            this.mOriginalTags = assignedTags;
            this.mSelectedTags = assignedTags;
            for (const assignedTag of assignedTags) {
              this.mAvailableTags.push(assignedTag);
            }

            this.refreshTagsReloadDataAndShowDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  private refreshTagsReloadDataAndShowDialog(): void {
    this
      .mTagService
      .getUnassignedTagInstancesFiltered('', 100)
      .then(
        availableTags => {
          if (!availableTags) {
            availableTags = [];
          }
          // Add the selectedTags to the available tags so we can deselect it if we need to
          if (this.mSelectedTags != null) {
            for (const selectedTag of this.mSelectedTags) {
              availableTags.push(selectedTag);
            }
          }
          this.mAvailableTags = availableTags;

          // Need to add the tag(s) the People instance has (if any) as well
          const numberAvailableTags = this.mAvailableTags.length;
          console.log("Available Tags: " + numberAvailableTags);

          const numberSelectedTags = (this.mSelectedTags) ? this.mSelectedTags.length : 0;
          console.log("Tags assigned: " + numberSelectedTags);

          // Create the list of emPeoples that this user has
          //this.multiSelectTagSelectedItems = [];
          //for (const tag of this.mSelectedTags) {
          // this.multiSelectTagSelectedItems.push(new DropDownItem(tag.id, tag.name));
          //}

          //Refresh the Instance Data, loading the existing values in for the Instance being edited
          //this.reloadInstanceData(true);
          this.mEntityInstanceDialogComponent.refreshData();

          //Show the Entity Instance Dialog
          this.mEntityInstanceDialogComponent.showDialog();
        },
        error => this.handleError(error)
      );
  }

  async createOrUpdateEntityInstance(): Promise<void> {
    //Set the values to the People Instance to be created
    this.mInstance.object = this.mInstanceEntityType;

    try {
      let person = null;
      if (this.mInstance.id == null) {
        //ID is null, create a new People Object Instance
        person = await this.mObjectService.createObjectInstance(this.mInstance);
      } else {
        // ID exists, update the People Object Instance
        person = await this.mObjectService.updateObjectInstance(this.mInstance);
      }

      // Person created or updated successfully!  If there is a tag/tags selected, assign it now...
      try {
        // Figure out if the tags originally assigned are different than the tags selected
        let tagsDifferent = false;

        if (this.mSelectedTags.length != this.mOriginalTags.length) {
          tagsDifferent = true;
        }

        if (this.mSelectedTags.length > 0 && this.mOriginalTags.length > 0) {
          for (const selectedTag of this.mSelectedTags) {
            if (!this.mOriginalTags.find(tag => selectedTag.id == tag.id)) {
              tagsDifferent = true;
              break;
            }
          }
        }

        if (tagsDifferent) {
          if (this.mInstance.id != null && this.mOriginalTags.length > 0) {
            // Unassign tags if this is an updated person and the person had tags (and they're different)
            await this.mObjectService.unassignAllTagsFromObjectInstance(person.id);
          }

          // assign the new selected tags
          await this.mObjectService.setAssignedTagsForObjectInstance(person.id, this.mSelectedTags);
        }

      } catch (error) {
        alert("People created but problems assigning Tag: " + error.text());
      } finally {
        this.refreshInstances();
        this.mEntityInstanceDialogComponent.hideDialog();
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  selectedEntityTypeChanged(entityType: ObjectDefinition): void {
    this.mInstanceEntityType = entityType;
    if (this.mInstanceEntityType != null && this.mInstanceEntityType.properties == null) {
      this
        .mObjectService
        .getObjectForId(this.mInstanceEntityType.id)
        .then(
          foundEntityType => {
            this.mInstanceEntityType.functions = foundEntityType.functions;
            this.mInstanceEntityType.properties = foundEntityType.properties;
            this.mEntityInstanceDialogComponent.refreshData();
          },
          error => this.handleError(error)
        );
    } else {
      this.mEntityInstanceDialogComponent.refreshData();
    }
  }

  selectedTagChanged(tagInstance: TagInstance[]): void {
    this.mSelectedTags = tagInstance;
  }

  /**
   * Called (debounced) when the User types in the Tag selector's search box. Loads the top 100
   * unassigned Tags whose Name matches the filter from the server, so the full set of Tags is
   * never loaded at once. The currently selected Tags are kept available so they can be deselected.
   */
  tagFilterChanged(filterString: string): void {
    this
      .mTagService
      .getUnassignedTagInstancesFiltered(filterString, 100)
      .then(
        availableTags => {
          if (!availableTags) {
            availableTags = [];
          }
          // Keep the selected Tags available so they can still be deselected while filtering
          if (this.mSelectedTags != null) {
            for (const selectedTag of this.mSelectedTags) {
              if (!availableTags.find(tag => tag.id == selectedTag.id)) {
                availableTags.push(selectedTag);
              }
            }
          }
          this.mAvailableTags = availableTags;
        },
        error => this.handleError(error)
      );
  }

  refreshInstanceData(): void {
    console.log("refreshInstanceData called");
    this
      .mObjectService
      .getObjectInstanceForId(this.mInstance.id)
      .then(
        response => {
          this.mInstance.data = response.data;
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(response.data);
          this.mEntityInstanceDialogComponent.refreshData();
        },
        error => this.handleError(error)
      );
  }


  deleteConfirmed(): void {
    if (this.mDeleteFlag == 1) {  //1 = Peoples
      this
        .mObjectService
        .deleteObjects(this.getSelectedPeopleTypeIds())
        .then(
          response => {
            this.refreshTypes();
          },
          error => this.handleError(error)
        );
    } else if (this.mDeleteFlag == 2) { //2 = People Instances
      this
        .mObjectService
        .deleteObjectInstances(this.getSelectedPeopleInstanceIds())
        .then(
          success => {
            this.refreshInstances();
          },
          error => this.handleError(error)
        );
    }
  }

  handleError(error: any): void {
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      //alert(error.error);
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }  
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

  // Returns a number as an array so that looping can be done in an ngFor in Angular
  numberAsArray(value: number): number[] {
    return Array(value).fill(0).map((x, i) => i);
  }


  uploadTypeFileClicked(): void {
    this.mTypeFileUploadInput.nativeElement.click();
  }

  onTypeFileUploadChange(event: any): void {
    if (event.target.files.length === 1) {
      this.uploadTypes(event.target.files[0]);
    }
  }

  uploadTypes(file: any): void {
    const formData = new FormData();
    formData.append("file", file);

    this.mObjectService.uploadTypes(formData).then(
      response => {
        console.log('Success');
      },
      error => {
        console.error(error);
      }
    );
  }

  downloadTypes(): void {
    this.mObjectService.downloadTypes().then(
      response => {
        const blob = new Blob([response], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        document.body.appendChild(a);
//           a.style = 'diplay: none';
        a.href = url;
        a.download = 'Peopletypes.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error => {
        console.error(error);
      }
    );
  }

  uploadInstanceFileClicked(): void {
    this.mInstanceFileUploadInput.nativeElement.click();
  }

  onInstanceFileUploadChange(event: any): void {
    if (event.target.files.length === 1) {
      this.uploadInstances(event.target.files[0]);
    }
  }

  private async uploadInstances(file: any):Promise<void> {
    const formData = new FormData();
    formData.append("file", file);

    try {
      await this.mObjectService.uploadInstances(formData);
      this.refreshInstances();
      console.log("Successfully uploaded instances !");
    } catch(error) {
      this.handleError(error);
    }
  }

  downloadPeople(): void {
    const selectedType = this.mPeopleTypeUis.filter(People => People.mSelected)[0];

    this.mObjectService.downloadInstances(selectedType.mPeopleType.id).then(
      response => {
        const blob = new Blob([response], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        document.body.appendChild(a);
//           a.style = 'diplay: none';
        a.href = url;
        a.download = 'Peopleinstances.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error => {
        console.error(error);
      }
    );
  }

  getPathForPersonImage(person: ObjectInstance): string {
    return this.mImageService.getImageSourceForObjectInstance(person);
  }


  /**-------------------------------------------------------------------------------------------------------------------- */

  // Called by <app-filter-bar> whenever the active filter or its value changes.
  onTypesFilterChanged(option: FilterOption): void {
    this.filterOptionForTypes = option;
    this.currentPageForTypes = 1;
    this.refreshTypes();
  }

  // Called by <app-filter-bar> whenever the active filter or its value changes.
  onInstancesFilterChanged(option: FilterOption): void {
    this.filterOptionForInstances = option;
    this.currentPageForInstances = 1;
    this.refreshInstances();
  }

  /*  
*  Handling the Right click event only on Asset Instance Name Column
*/
  fetchFunctionList(event: MouseEvent, peopleInstance: ObjectInstance): void {
    // Stop the default handling of right click event
    event.preventDefault();
    this.showContextMenu = true;
    this.showGeneralDialog = false;

    // Set the x & y co-ordinates for the context menu to open up
    this.contextMenuPosition.x = event.clientX;
    this.contextMenuPosition.y = event.clientY;

    // Empty the function List if any element still present
    this.functionList = []

    // Send the request to get the Reader Instance details using the ID
    this
      .mObjectService
      .getObjectInstanceForId(peopleInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the Entity Instance Dialog to the Instance that was returned
          this.mInstance = response;

          // Create the function List for the context menu from the response received 
          try {
            if (response.data && Array.isArray(response.object.functions) && (response.object.functions).length > 0) {
              this.functionList = response.object.functions;

            } else {
              // Input a dummy

              this.functionList = INFRA_DUMMY_FUNCTION;
            }
          }
          catch (error) {
            this.handleError(error);
          }
        },
        error => this.handleError(error)
      );

  }

  onItemClicked(item: any) {
    // Handle the click in the dropdown menu item here 
    this.selectedFunction = item;

    if (item.id != -1) {
      // Display the Dialog Box for confirmation
      this.showGeneralDialog = true;
    }

    // Hide the Context Menu/Dropdown
    this.showContextMenu = false;

  }

}

class PeopleObjectUI {
  mSelected = false;
  mPeopleType: ObjectDefinition;
}

class PeopleInstanceUI {
  mSelected = false;
  mPeopleInstance: ObjectInstance;
}